import { memo, useRef, useState } from 'react';
import { isObject } from 'underscore';

import OptionalComponent from '@ui/optional-component/optional-component';
import handleImageUploadErrors from '@ui/image-upload/handle-image-upload-errors';
import UploadLarge from '@ui/uploads/upload-large';
import {
  GetUploadEndpoint,
  getUploadFilename,
  UploadFileToProvider,
  SetItemContentType,
} from '@ui/uploads/validations';
import Container from '@ui/container/container';

import { EPermissions } from '@/types/permissions.types';
import useHasPermission from '@/utils/authentication/use-has-permission';
import Icon from '@ui/icons/icon';
import { ICONS } from '@ui/icons/types';
import useWorkingProjectColorsByV2Names from '@/utils/use-working-project-colors-by-v2-names';
import getRelativeContrastColor from '@/utils/colors/generate-relative-contrasting-color';
import Text from '@ui/text/text/text';
import getThemeColorCSSVariable from '@/utils/colors/get-theme-color-css-variable';

import styles from './project-image.module.css';

type ProjectImageProps = {
  onUpload: (url: string) => Promise<void>;
  onDelete?: () => void;
  img?: string | null;
  style?: React.CSSProperties;
  disabled?: boolean;
  height?: string;
  width?: string;
  FooterContent?: React.FC | null;
};

const ProjectImage = memo(
  ({
    disabled,
    onUpload,
    onDelete,
    img,
    style,
    height = '230px',
    width = '410px',
    FooterContent,
  }: ProjectImageProps) => {
    const { primary: primaryColor } = useWorkingProjectColorsByV2Names();

    const uploadInputRef = useRef<HTMLInputElement | null>(null);
    const [uploading, setUpLoading] = useState(false);

    const { hasPermission } = useHasPermission({ allowedRoles: [EPermissions.GroupAdmin, EPermissions.Editor] });
    const _disabled = disabled || !hasPermission;

    const handleFile = async (file: File | FileList) => {
      try {
        if (file instanceof File) {
          setUpLoading(true);

          const endpoint = await GetUploadEndpoint({
            filename: getUploadFilename(file.name),
          });

          if (isObject(endpoint) && endpoint.url) {
            const finalUrl = await UploadFileToProvider(
              {
                url: endpoint.url,
                fields: endpoint.fields,
                cdnHost: endpoint?.cdnHost,
              },
              file
            );

            SetItemContentType({ filename: finalUrl, contentType: file.type }).catch(console.error);

            if (finalUrl) {
              await onUpload(finalUrl);
            }
          }
        }
      } catch (error) {
        console.error(error);
        handleImageUploadErrors(error);
      } finally {
        setUpLoading(false);
      }
    };

    return (
      <div className={`width-auto ${styles.projectImage}`}>
        <div style={{ position: 'relative', height, width }}>
          <div
            style={{ position: 'absolute', top: 0, left: 0, height, width }}
            className="flex-column jc-center ai-center width-100-percent height-100-percent"
          >
            <UploadLarge
              displayImageUrl={img}
              editable={hasPermission}
              deletable={hasPermission}
              disabled={_disabled}
              uploading={uploading}
              ref={uploadInputRef}
              style={{ ...(style || {}) }}
              contrastRegionCheck={
                img || !primaryColor?.[0]
                  ? undefined
                  : {
                      sampleRegion: {
                        x: 33,
                        y: 33,
                        width: 33,
                        height: 33,
                        units: 'pct',
                      },
                      foregroundColor: primaryColor[0],
                    }
              }
              CustomTitle={({ hasValidContrast }) => {
                return (
                  <>
                    <OptionalComponent display={!img}>
                      <div className="flex-column jc-center ai-center height-100-percent width-fit-content margin-auto overlow-hidden radius-10 padding-8">
                        <Text
                          className="L2 flex-column jc-center ai-center"
                          color={
                            hasValidContrast?.hasValidContrast === false
                              ? getRelativeContrastColor(
                                  primaryColor?.[0] || '',
                                  hasValidContrast.backgroundMode === 'dark' ? '#000000' : '#ffffff'
                                )
                              : getThemeColorCSSVariable()
                          }
                        >
                          <Icon name={ICONS.PHOTO} size={17} />
                          <OptionalComponent display={hasPermission}>
                            <div>Add image</div>
                          </OptionalComponent>
                        </Text>
                      </div>
                    </OptionalComponent>
                  </>
                );
              }}
              onFile={handleFile}
              onDelete={onDelete}
              allowedFileTypes={['image/jpg', 'image/jpeg', 'image/png', 'image/gif']}
            />
            {FooterContent ? (
              <Container
                gradientOverlay={{
                  height: '100%',
                  background: 'linear-gradient(to top, rgba(var(--gray-1200-rgb), 0.7), rgba(var(--gray-1200-rgb), 0))',
                  borderRadius: '0 0 8px 8px',
                  width: img ? '100%' : 'calc(100% - 2px)',
                  left: img ? 0 : '1px',
                  right: img ? 0 : '1px',
                  bottom: img ? 0 : '1px',
                }}
                width="100%"
                height="30%"
                className={`position-absolute bottom-0 left-0 right-0 cursor-pointer ${styles.footer}`}
                onClick={() => {
                  // click the input to open the file picker
                  uploadInputRef?.current?.click();
                }}
              >
                <FooterContent />
              </Container>
            ) : null}
          </div>
        </div>
      </div>
    );
  }
);

export default ProjectImage;
